import { useAdminAuth } from "@/contexts/useAdminAuth";
import { api } from "@/lib/api";
import type { OrganizationDetail } from "@/types";
import { Badge, Button, Text } from "@a7y/tailwind-catalyst";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, Check, Copy, Pencil } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BillingTab } from "./org-detail/BillingTab";
import { EditOrgNameDialog } from "./org-detail/EditOrgNameDialog";
import { MembersTab } from "./org-detail/MembersTab";
import { OverviewTab } from "./org-detail/OverviewTab";
import { PlanTab } from "./org-detail/PlanTab";
import { ProjectsTab } from "./org-detail/ProjectsTab";
import { SupportAccessTab } from "./org-detail/SupportAccessTab";
import { visibleOrgDetailTabs, type OrgDetailTab } from "./org-detail/org-detail-tabs";
import { PLAN_COLORS, STATUS_COLORS } from "./org-detail/constants";

type Tab = OrgDetailTab;

export function OrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as Tab) || "overview";
  const setActiveTab = (tab: Tab) => setSearchParams({ tab });

  const [copied, setCopied] = useState(false);
  const [showEditName, setShowEditName] = useState(false);

  const { data, isLoading, error } = useQuery<OrganizationDetail>({
    queryKey: ["organization-detail", orgId],
    queryFn: () => api.getOrganizationDetail(orgId!),
    enabled: !!orgId,
  });

  const invalidateDetail = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["organization-detail", orgId] }),
    [queryClient, orgId],
  );

  const handleCopyOrgId = () => {
    if (!orgId) return;
    navigator.clipboard.writeText(orgId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-4 py-2">
        <Button plain onClick={() => navigate("/admin/organizations")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
        <div className="mt-8 text-center">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <Text className="mt-2 text-gray-500 dark:text-zinc-400">Organization not found</Text>
        </div>
      </div>
    );
  }

  const { organization: org, ledger, usage, members, transactions, projects } = data;
  // A7Y-1833 — the primary badge shows the BILLED plan so it matches the org
  // list; the separate OVERRIDE badge (below) marks any admin grant.
  const billedPlan = ledger?.planId || "free";

  return (
    <div className="px-4 py-2">
      {/* Header */}
      <div className="mb-8">
        <Button plain onClick={() => navigate("/admin/organizations")} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
            <Building2 className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Text className="text-3xl font-bold text-zinc-950 dark:text-white">{org.name}</Text>
              <button
                type="button"
                onClick={() => setShowEditName(true)}
                className="text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                title="Edit organization name"
                aria-label="Edit organization name"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <Text className="text-sm font-mono text-zinc-400 dark:text-zinc-500">{org.id}</Text>
              <button
                onClick={handleCopyOrgId}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                title="Copy organization ID"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <Badge color={PLAN_COLORS[billedPlan] ?? "zinc"}>
              {billedPlan.toUpperCase()}
            </Badge>
            {ledger?.overridePlanId && <Badge color="amber">OVERRIDE</Badge>}
            <Badge color={STATUS_COLORS[ledger?.status ?? ""] ?? "zinc"}>
              {(ledger?.status ?? "no ledger").toUpperCase().replace("_", " ")}
            </Badge>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="mb-6 flex gap-0 border-b border-zinc-200 dark:border-zinc-700">
        {visibleOrgDetailTabs(isSuperAdmin).map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.value
                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && <OverviewTab org={org} usage={usage} ledger={ledger} />}
      {activeTab === "plan" && <PlanTab orgId={orgId!} ledger={ledger} onLedgerChange={invalidateDetail} />}
      {activeTab === "billing" && <BillingTab transactions={transactions} />}
      {activeTab === "members" && <MembersTab members={members} />}
      {activeTab === "support-access" && isSuperAdmin && <SupportAccessTab orgId={orgId!} />}
      {activeTab === "projects" && <ProjectsTab orgId={orgId!} projects={projects || []} />}

      <EditOrgNameDialog
        open={showEditName}
        orgId={orgId!}
        currentName={org.name}
        onClose={() => setShowEditName(false)}
        onSuccess={() => {
          setShowEditName(false);
          invalidateDetail();
          queryClient.invalidateQueries({ queryKey: ["organizations"] });
        }}
      />
    </div>
  );
}
